import React, { useEffect, useState } from "react";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import PropTypes from "prop-types";
import "./index.scss";
import Info from "~components/Form/components/Info";
import { getChartColor } from "./chartColors";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

const fmtNumero = (value) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value ?? 0);

const BarChart = ({
  data,
  escolha,
  filters,
  items,
  tipo,
  total,
  metaInfo = "Meta de consumo desse cliente para cada produto no mês atual",
}) => {
  const [modifiedData, setModifiedData] = useState(data || { labels: [], groups: [] });

  const soma = (array) => {
    if (!array || !Array.isArray(array) || array.length === 0) {
      return 0;
    }
    const divisor = array.slice(1).filter((num) => num > 0).length || 1;
    const soma = array.slice(1).reduce((acumulador, valor) => acumulador + valor, 0);
    const result = soma / divisor;
    return Number.parseFloat(result.toFixed(2));
  };

  const hexToRgba = (hex, opacity) => {
    const parsedHex = hex.replace("#", "");
    const bigint = Number.parseInt(parsedHex, 16);
    const r = (bigint >> 16) & 255;
    const g = (bigint >> 8) & 255;
    const b = bigint & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  const computeDatasetColors = (backgroundColor, labels) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentYear = currentDate.getFullYear().toString().slice(-2);
    return (labels || []).map((label) => {
      const match = label && label.match ? label.match(/^(\d{2})\/(\d{2})$/) : null;
      if (match) {
        const month = Number.parseInt(match[1], 10);
        const year = match[2];
        if (month === currentMonth && year === (currentYear - 1).toString()) {
          return hexToRgba(backgroundColor, 0.5);
        }
      }
      return label === "Meta mês" ? hexToRgba(backgroundColor, 0.5) : backgroundColor;
    });
  };

  const applySoftScale = (value, max) => {
    if (value === null) return null;
    const minVisualFactor = 0.2;
    return (value / max) * (1 - minVisualFactor) + minVisualFactor;
  };

  const buildChartDatasets = (modifiedDataParam, useSoftScale = false) => {
    const groups = (modifiedDataParam && modifiedDataParam.groups) || [];
    const labels = (modifiedDataParam && modifiedDataParam.labels) || [];
    const maxValue = data && data.groups && data.groups.length > 0 ? Math.max(...data.groups.flatMap((g) => g[escolha])) : 1;

    return groups.map((group) => {
      const backgroundColor = getChartColor(group);
      const datasetColors = computeDatasetColors(backgroundColor, labels);

      const adjustedData = (group?.[escolha] ?? []).map((v) => {
        if (!useSoftScale) return v;
        if (v === null) return null;
        return applySoftScale(v, maxValue) * maxValue;
      });

      return {
        label: group.name,
        data: adjustedData,
        backgroundColor: datasetColors,
        borderColor: filters.grupoMaterialId === 0 ? backgroundColor : "#000000",
        borderWidth: filters.grupoMaterialId === 0 ? 0 : 3,
        stack: useSoftScale ? "Stack 1" : "Stack 0",
        minBarLength: 20,
      };
    });
  };

  const handleTransform = (groups) => {
    return groups.map((item) => ({
      ...item,
      [escolha]: item[escolha].map((volume) => (volume === 0 ? null : volume)),
    }));
  };

  useEffect(() => {

    if (data?.groups?.length > 0) {
      let modifiedData = {
        labels: [...data.labels],
        groups: data.groups.map((group) => ({
          ...group,
          [escolha]: Array.isArray(group[escolha]) ? [...group[escolha]] : [],
        })),
      };
      if (data.govs && data.govs.length > modifiedData.groups.length) {
        for (const gov of data.govs) {
          if (!modifiedData.groups.some((group) => group.id === gov.id)) {
            const arrayVazio = new Array(modifiedData.labels.length).fill(null);
            modifiedData.groups.push({
              id: gov.id,
              name: gov.name,
              [escolha]: arrayVazio,
            });
          }
        }
      }

      let filtroProdutoMeta = 0;

      if (!modifiedData.labels.includes("Meta mês") && data.govs && escolha !== "volumesDtus") {
        modifiedData.labels.push("Meta mês");
        modifiedData.groups = modifiedData.groups.map((group, groupIndex) => {
          const gov = data.govs.find((gov) => gov.id === group.id);
          if (!gov || (filtroProdutoMeta > 0 && group.filtro === "produto")) return group;
          filtroProdutoMeta++;
          return {
            ...group,
            [escolha]: [...group[escolha], gov[escolha]],
          };
        });
      }

      modifiedData.groups = handleTransform(modifiedData.groups);
      setModifiedData(modifiedData);
    } else {
      setModifiedData({ labels: [], groups: [] });
    }
  }, [data, escolha]);

  const chartData = {
    labels: modifiedData?.labels || [],
    datasets: buildChartDatasets(modifiedData, true),
  };

  const chartDataOption = {
    labels: modifiedData?.labels || [],
    datasets: buildChartDatasets(modifiedData, false),
  };

  const formatShort = (value) => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace(".", ",")}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(1).replace(".", ",")}k`;
    const isVolume = escolha === "volumes" || escolha === "volumesDtus";
    return isVolume ? value.toFixed(2).replace(".", ",") : value.toFixed(0);
  };

  const labelSums = (chartDataOption.labels || []).map((label, index) =>
    chartDataOption.datasets.reduce((acc, dataset) => {
      const value = dataset.data[index];
      if (typeof value === "number" && !Number.isNaN(value)) return acc + value;
      return acc;
    }, 0)
  );

  const options = {
    layout: {
      padding: {
        top: 8,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          drawBorder: false,
          drawTicks: false,
          lineWidth: 1,
          color: "#e0e0e0",
        },
        ticks: {
          display: false,
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: "#01054C",
          font: {
            weight: "bold",
          },
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        color: "#FFFFFF",
        font: {
          weight: "bold",
        },
        formatter: (value, context) => {
          const group = modifiedData.groups[context.datasetIndex];
          const originalValue = group[escolha][context.dataIndex];
          if (originalValue === null || originalValue === undefined || originalValue === 0) {
            return "";
          }
          return formatShort(originalValue);
        },
      },
      tooltip: {
        callbacks: {
          label: function (tooltipItem) {
            const group = modifiedData.groups[tooltipItem.datasetIndex];
            const originalValue = group[escolha][tooltipItem.dataIndex];
            return ` ${group.name}: ${fmtNumero(originalValue)}`;
          },
        },
      },
    },
  };

  const getFormattedAverage = () => {
    const sums = chartDataOption.labels.map((label, index) => {
      const sum = chartDataOption.datasets.reduce((acc, dataset) => {
        const value = dataset.data[index];
        if (typeof value === "number" && !Number.isNaN(value)) {
          return acc + value;
        }
        return acc;
      }, 0);
      return sum;
    });


    const temColunaMeta = chartDataOption.labels[chartDataOption.labels.length - 1] === "Meta mês";
    const sliced = sums.slice(1, temColunaMeta ? -1 : undefined);
    const filtered = sliced.filter((val) => val !== 0);
    const total = filtered.reduce((acc, val) => acc + val, 0);
    const count = filtered.length;

    const average = count > 0 ? total / count : 0;
    return fmtNumero(average);
  };

  const formattedAverage = getFormattedAverage();

  const getTitleText = () => {
    if (tipo === "linha") {
      return escolha === "liquidos" ? "Fob. Líquido (R$/t)" : "Faturamento Médio";
    }
    return "Volume Médio";
  };

  const getUnitSuffix = () => {
    if (escolha === "volumes") {
      return " t/mês";
    }
    return filters?.grupoMaterialId === 0 ? " t/DU" : " t/DU/mês ";
  };

  const getValueText = () => {
    if (tipo === "linha") {
      if (escolha === "liquidos") {
        return "-";
      }
      return Number.isFinite(total) ? fmtNumero(total) : "0,00";
    }

    const unitSuffix = getUnitSuffix();
    return formattedAverage + unitSuffix;
  };

  const getItemValueText = (item) => {
    if (tipo === "linha") {
      return fmtNumero(soma(item[escolha]));
    }

    const unitSuffix = getUnitSuffix();
    return fmtNumero(soma(item[escolha])) + unitSuffix;
  };

  return (
    <div data-testid="bar-chart-container">
      <Bar data={chartData} options={options} data-testid="bar-chart" />
      {filters?.grupoMaterialId !== 0 && (
        <div className="rtz_values" data-testid="rtz-values">
          {chartDataOption.labels.map((label, index) => {
            const sum = labelSums[index] || 0;
            return (
              <div key={label} className=" p-2 rounded-lg  max-w-[200px] " data-testid={`value-item-${index}`}>
                <p className="text-xs font-bold">{fmtNumero(sum)}</p>
              </div>
            );
          })}
        </div>
      )}
      <div
        className={`px-8 relative ${modifiedData.labels.includes("Meta mês") ? "" : "pt-[30px]"}`}
        data-testid="chart-summary"
      >
        {escolha !== "volumesDtus" && modifiedData.labels.includes("Meta mês") && (
          <div className="relative w-full flex justify-end top-[10px]" data-testid="meta-info-container">
            <Info bg="blue" text={metaInfo} position="left" />
          </div>
        )}

        <div
          className="flex flex-col md:flex-row w-full items-center justify-between gap-2 bg-[#F2F2F2] p-2 rounded-md mt-8 pl-5"
          data-testid="chart-title-section"
        >
          <p className="text-primaria text-xl font-bold" data-testid="chart-title">
            {getTitleText()}
          </p>
          <span className="bg-terciaria rounded-md w-full md:w-auto text-center" data-testid="chart-value-container">
            <p className="text-white text-xl font-bold py-2 px-4" data-testid="chart-value">
              {getValueText()}
            </p>
          </span>
        </div>
        <div className="mt-8 grid grid-cols-2 gap-2" data-testid="chart-legend">
          {(items || []).map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className="flex items-center gap-4 first:mt-0"
              data-testid={`legend-item-${item.id}`}
            >
              <span
                className="w-5 h-5 rounded-full relative"
                data-testid={`legend-color-${item.id}`}
                style={{
                  backgroundColor: getChartColor(item),
                  border: `${tipo === "barra" && filters?.grupoMaterialId === 0 ? "none" : "2px solid #000000"}`,
                  maxWidth: "20px",
                  minWidth: "20px",
                  height: "20px",
                }}
              >
                {tipo === "linha" && (
                  <p
                    className="roteirizacao__line"
                    data-testid={`legend-line-${item.id}`}
                    style={{
                      backgroundColor: getChartColor(item),
                    }}
                  ></p>
                )}
              </span>
              <p className="text-primaria font-bold text-[12px]" data-testid={`legend-text-${item.id}`}>
                {item.name}: {getItemValueText(item)}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

BarChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.array,
    groups: PropTypes.arrayOf(PropTypes.object),
    govs: PropTypes.arrayOf(PropTypes.object),
  }),
  escolha: PropTypes.string.isRequired,
  filters: PropTypes.shape({
    grupoMaterialId: PropTypes.number,
  }),
  items: PropTypes.arrayOf(PropTypes.object),
  tipo: PropTypes.string,
  total: PropTypes.number,
  metaInfo: PropTypes.string,
};

export default BarChart;
